import React from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    Divider,
    Grid,
    Card,
    CardContent,
    Alert,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Link,
    Chip
} from '@mui/material';
import {
    Shield,
    // Database,
    CreditCard,
    Lock,
    Visibility,
    Description,
    Language,
    Email,
    LocationOn,
    FiberManualRecord,
    CheckCircle
} from '@mui/icons-material';

const PrivacyPolicy = () => {
    return (
        <Container maxWidth="lg" sx={{ py: 6 }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Typography
                    variant="h3"
                    gutterBottom
                    sx={{
                        fontWeight: 700,
                        color: 'primary.main',
                        textAlign: 'center',
                        mb: 2
                    }}
                >
                    Privacy Policy
                </Typography>

                <Typography
                    variant="subtitle1"
                    sx={{
                        textAlign: 'center',
                        color: 'text.secondary',
                        fontStyle: 'italic',
                        mb: 4
                    }}
                >
                    Last updated: June 28, 2025
                </Typography>

                <Divider sx={{ mb: 4 }} />

                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 4 }}>
                    At <strong>National Youth Cooperative Society (NYCS) Surat</strong>, we are committed to protecting your privacy.
                    This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website.
                </Typography>

                {/* Section 1 - Information We Collect */}
                <Box sx={{ mt: 4 }}>
                    <Typography
                        variant="h5"
                        gutterBottom
                        sx={{
                            fontWeight: 600,
                            color: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            mb: 2
                        }}
                    >
                        {/* <Database sx={{ mr: 1 }} /> */}
                        1. Information We Collect
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ ml: 4, mb: 3 }}>
                        When you visit or interact with the NYCS Surat website, we may collect the following personal and non-personal information:
                    </Typography>
                    <Grid container spacing={2} sx={{ ml: 2 }}>
                        {[
                            'Full name',
                            'Date of birth',
                            'Contact information (email, phone)',
                            'Address details',
                            'Identification documents',
                            'Educational/professional details',
                            'Bank details (when required)',
                            'Uploaded documents'
                        ].map((item, index) => (
                            <Grid item xs={12} sm={6} key={index}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <FiberManualRecord sx={{ color: 'primary.main', fontSize: 12, mr: 2 }} />
                                    <Typography variant="body2">{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Section 2 - How We Use Your Information */}
                <Box sx={{ mt: 4 }}>
                    <Typography
                        variant="h5"
                        gutterBottom
                        sx={{
                            fontWeight: 600,
                            color: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            mb: 2
                        }}
                    >
                        {/* <Visibility sx={{ mr: 1 }} /> */}
                        2. How We Use Your Information
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ ml: 4, mb: 2 }}>
                        We use the information you provide for purposes including:
                    </Typography>
                    <List sx={{ ml: 2 }}>
                        {[
                            'Membership processing',
                            'Opening saving accounts',
                            'Digital payment verification',
                            'Training or employment program enrollment',
                            'Internal record-keeping',
                            'Legal or regulatory compliance',
                            'Responding to queries or concerns'
                        ].map((item, index) => (
                            <ListItem key={index} sx={{ py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    <FiberManualRecord sx={{ color: 'success.main', fontSize: 12 }} />
                                </ListItemIcon>
                                <ListItemText primary={item} />
                            </ListItem>
                        ))}
                    </List>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Section 3 - Payment Information */}
                <Box sx={{ mt: 4 }}>
                    <Typography
                        variant="h5"
                        gutterBottom
                        sx={{
                            fontWeight: 600,
                            color: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            mb: 2
                        }}
                    >
                        {/* <CreditCard sx={{ mr: 1 }} /> */}
                        3. Payment Information
                    </Typography>
                    <Box sx={{ ml: 4 }}>
                        <Typography variant="body1" paragraph>
                            All payments are processed digitally. We use <Chip label="Razorpay" color="primary" size="small" /> as our payment gateway,
                            which adheres to industry-standard security protocols.
                        </Typography>
                        <Alert severity="success" sx={{ mt: 2 }}>
                            <Typography variant="body2">
                                🔒 NYCS Surat does not store any sensitive payment information like debit/credit card numbers or CVVs.
                            </Typography>
                        </Alert>
                    </Box>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Section 4 - Data Storage and Security */}
                <Box sx={{ mt: 4 }}>
                    <Typography
                        variant="h5"
                        gutterBottom
                        sx={{
                            fontWeight: 600,
                            color: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            mb: 2
                        }}
                    >
                        {/* <Lock sx={{ mr: 1 }} /> */}
                        4. Data Storage and Security
                    </Typography>
                    <Box sx={{ ml: 4 }}>
                        <Typography variant="body1" paragraph>
                            Your data is stored securely on <Chip label="Firebase" color="primary" size="small" /> infrastructure, including:
                        </Typography>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={12} md={6}>
                                <Card sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            Firestore Database
                                        </Typography>
                                        <Typography variant="body2">
                                            Secure storage for form data and user information
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Card sx={{ bgcolor: 'secondary.light', color: 'secondary.contrastText' }}>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            Firebase Storage
                                        </Typography>
                                        <Typography variant="body2">
                                            Encrypted storage for document uploads
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                        <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                            We follow strict access control and data protection measures to ensure the safety of your personal information.
                        </Typography>
                    </Box>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Section 5 - Disclosure of Your Information */}
                <Box sx={{ mt: 4 }}>
                    <Typography
                        variant="h5"
                        gutterBottom
                        sx={{
                            fontWeight: 600,
                            color: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            mb: 2
                        }}
                    >
                        {/* <Shield sx={{ mr: 1 }} /> */}
                        5. Disclosure of Your Information
                    </Typography>
                    <Box sx={{ ml: 4 }}>
                        <Typography variant="body1" paragraph>
                            We do not sell or share your personal information with third parties unless:
                        </Typography>
                        <List>
                            {[
                                'Required by law or government regulation',
                                'Required to complete a payment or registration process',
                                'Explicitly consented by you'
                            ].map((item, index) => (
                                <ListItem key={index}>
                                    <ListItemIcon>
                                        <FiberManualRecord sx={{ color: 'error.main', fontSize: 12 }} />
                                    </ListItemIcon>
                                    <ListItemText primary={item} />
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Section 6 - Your Rights */}
                <Box sx={{ mt: 4 }}>
                    <Typography
                        variant="h5"
                        gutterBottom
                        sx={{
                            fontWeight: 600,
                            color: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            mb: 2
                        }}
                    >
                        {/* <Description sx={{ mr: 1 }} /> */}
                        6. Your Rights
                    </Typography>
                    <Box sx={{ ml: 4 }}>
                        <Typography variant="body1" paragraph>
                            You may:
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <Card sx={{ bgcolor: 'warning.light', height: '100%' }}>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom color="warning.dark">
                                            📋 Data Access
                                        </Typography>
                                        <Typography variant="body2" color="warning.dark">
                                            Request a copy of your submitted data
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Card sx={{ bgcolor: 'info.light', height: '100%' }}>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom color="info.dark">
                                            ✏️ Data Correction
                                        </Typography>
                                        <Typography variant="body2" color="info.dark">
                                            Request correction of incorrect data
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Card sx={{ bgcolor: 'error.light', height: '100%' }}>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom color="error.dark">
                                            🗑️ Data Deletion
                                        </Typography>
                                        <Typography variant="body2" color="error.dark">
                                            Request deletion of your data (subject to legal obligations)
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                        <Alert severity="info" sx={{ mt: 3 }}>
                            <Typography variant="body2">
                                To make a data-related request, contact us at:{' '}
                                <Link href="mailto:nycsurat@gmail.com" color="primary" underline="hover">
                                  jananidhi.29surat@gmail.com
                                </Link>
                            </Typography>
                        </Alert>
                    </Box>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Section 7 - Third-Party Links */}
                <Box sx={{ mt: 4 }}>
                    <Typography
                        variant="h5"
                        gutterBottom
                        sx={{
                            fontWeight: 600,
                            color: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            mb: 2
                        }}
                    >
                        {/* <Language sx={{ mr: 1 }} /> */}
                        7. Third-Party Links
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ ml: 4 }}>
                        Our website may contain links to third-party websites. We are not responsible for the content
                        or privacy practices of those sites. We encourage you to review their privacy policies before
                        providing any personal information.
                    </Typography>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Section 8 - Updates to this Policy */}
                <Box sx={{ mt: 4 }}>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                        8. Updates to this Policy
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ ml: 4 }}>
                        We may update this Privacy Policy from time to time. Changes will be posted on this page
                        with the "last updated" date. We encourage you to review this policy periodically to stay
                        informed about how we protect your information.
                    </Typography>
                </Box>

                <Divider sx={{ my: 4 }} />

                {/* Contact Section */}
                <Box sx={{
                    mt: 4,
                    p: 3,
                    backgroundColor: 'primary.light',
                    borderRadius: 2,
                    color: 'primary.contrastText'
                }}>
                    <Typography
                        variant="h5"
                        gutterBottom
                        sx={{
                            fontWeight: 600,
                            mb: 2,
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        <Email sx={{ mr: 1 }} />
                        9. Contact Us
                    </Typography>
                    <Typography variant="body1" paragraph>
                        For any questions or concerns regarding this policy or your personal data, please contact:
                    </Typography>
                    <Paper sx={{ p: 3, mt: 2, bgcolor: 'background.paper' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', mb: 2 }}>
                            National Youth Cooperative Society (NYCS) Surat
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Email sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
                                <Typography variant="body1" color="text.primary">
                                    Email:{' '}
                                    <Link href="mailto:nycsurat@gmail.com" color="primary" underline="hover">
                                       jananidhi.29surat@gmail.com
                                    </Link>
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <LocationOn sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
                                <Typography variant="body1" color="text.primary">
                                    Address:G-26, Magnus Shopping Centre, Althan Bhimrad Canal Road, Althan Surat, 395017
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Language sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
                                <Typography variant="body1" color="text.primary">
                                    Website: https://nycsyuvasurat.com/
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Box>

                <Box sx={{ mt: 4, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        By using our services, you acknowledge that you have read and understood this Privacy Policy.
                    </Typography>
                </Box>
            </Paper>
        </Container>
    );
};

export default PrivacyPolicy;